import { create, v, w, node, invalidator } from '@dojo/framework/core/vdom';
import { createICacheMiddleware } from '@dojo/framework/core/middleware/icache';
import { dimensions } from '@dojo/framework/core/middleware/dimensions';
import { theme } from '@dojo/framework/core/middleware/theme';
import { DNode } from '@dojo/framework/core/interfaces';
import { CarouselNav } from './nav';
import { Keys } from '@holmescorp/common';
import { range } from 'lodash-es';
import { formatAriaProperties } from '@dojo/widgets/common/util';

// CSS
import css from '../css/carousel.m.css';

// Widget
export interface CarouselProperties {
	/** Width of the overall carousel; Optional, default: see - containerWidth, then CSS */
	width?: number;
	/** Optional title for the carousel */
	title?: string;
	/** Optional footnote */
	footnote?: string;
	/** Aria properties */
	aria?: { [key: string]: string | null };
	/** QA string; Optional, default: undefined */
	xqa?: string;
	/** Number of seats visible in the carousel; Optional, default: 4 */
	seatsVisible?: number;
	/** Optional content to show next to the title */
	legend?: string;
	/** Optional if screen size is small, use small header css */
	useSmallHeader?: boolean;
	/** Optional heading level for the title */
	headingLevel?: number;
}

export interface CarouselICache {
	active: number;
	translateDelta: number;
	seatsVisible: number;
	width: number;
}

const icache = createICacheMiddleware<CarouselICache>();

const factory = create({
	icache,
	theme,
	dimensions,
	node,
	invalidator
}).properties<CarouselProperties>();

export const Carousel = factory(
	({ id, properties, children, middleware: { icache, theme, dimensions, node, invalidator } }) => {
		const themeCss = theme.classes(css);
		const {
			width = 996,
			xqa,
			aria = { live: 'polite' },
			seatsVisible = 4,
			title,
			footnote,
			legend,
			useSmallHeader = false,
			headingLevel = 2
		} = properties();

		const widgetId = `carousel-${id}`;

		const active = icache.getOrSet('active', 0);
		const visibleSeats = icache.getOrSet('seatsVisible', seatsVisible);

		const recalculate = () => {
			const w = window.innerWidth;
			icache.set(
				'seatsVisible',
				() => (w < 996 && w > 768 ? 3 : w < 768 && w > 540 ? 2 : w < 540 ? 1 : seatsVisible),
				false
			);
			const seatWidth = dimensions.get(`seat-${active}`).size.width;
			const navWidth = 110;
			const newWidth =
				w < 996 && w > 768
					? seatWidth * 3 + navWidth
					: w < 768 && w > 540
					? seatWidth * 2 + navWidth
					: w < 540
					? seatWidth + navWidth
					: width;
			const e = node.get(widgetId);
			if (e) {
				e.style.width = `${newWidth}px`;
				invalidator();
			}
		};

		window.addEventListener('resize', recalculate);

		const kids = children();
		const numberOfSeats = kids.length;
		const items = kids.map((kid: DNode, index) => {
			const visible = range(active, active + visibleSeats);
			const hidden = !visible.includes(index);
			const cls = [themeCss.seat];
			if (hidden) {
				cls.push(themeCss.hidden);
			}
			return v(
				'div',
				{
					key: `seat-${index}`,
					role: 'listitem',
					...formatAriaProperties({ hidden: hidden.toString() }),
					classes: cls
				},
				[kid]
			);
		});

		const leftDisabled = active <= 0;
		const maxDelta = numberOfSeats - visibleSeats;
		const rightDisabled = active >= maxDelta;
		const translateDelta = icache.getOrSet('translateDelta', 0);
		recalculate();
		return v(
			'div',
			{
				key: widgetId,
				id,
				widgetId,
				xqa,
				...formatAriaProperties(aria),
				classes: [themeCss.root]
			},
			[
				title
					? v('div', { classes: [useSmallHeader ? themeCss.headerSmall : themeCss.header] }, [
							v(`h${headingLevel}`, { classes: [themeCss.title] }, [title]),
							legend ? v('div', { classes: [themeCss.legend], innerHTML: legend }) : null
					  ])
					: null,
				v('div', { classes: [themeCss.wrapper] }, [
					v('div', { key: 'leftNav', classes: [themeCss.navLeft] }, [
						w(CarouselNav, {
							direction: 'left',
							aria: {
								label: `Load previous page of content about ${title}`,
								disabled: leftDisabled.toString(),
								controls: id
							},
							disabled: leftDisabled,
							onClick: () => {
								if (active > 0) {
									const seatWidth = dimensions.get(`seat-${active}`).size.width;
									const prev = active - 1;
									icache.set('translateDelta', seatWidth! * prev);
									icache.set('active', prev);
								}
							},
							onKeyDown: (event: KeyboardEvent) => {
								switch (event.key) {
									case Keys.Enter:
									case Keys.Space:
									case Keys.Spacebar:
										event.preventDefault();
										if (active > 0) {
											const seatWidth = dimensions.get(`seat-${active}`).size.width;
											const prev = active - 1;
											icache.set('translateDelta', seatWidth! * prev);
											icache.set('active', prev);
										}
								}
							}
						})
					]),
					v('div', { classes: [themeCss.content] }, [
						v(
							'div',
							{
								key: 'seats',
								role: 'list',
								classes: [themeCss.seats],
								style: `transform: translateX(-${translateDelta}px)`
							},
							[...items]
						)
					]),
					v('div', { key: 'rightNav' }, [
						w(CarouselNav, {
							direction: 'right',
							disabled: rightDisabled,
							aria: {
								label: `Load next page of content about ${title}`,
								disabled: rightDisabled.toString(),
								controls: id
							},
							onClick: () => {
								if (active < maxDelta) {
									const seatWidth = dimensions.get(`seat-${active}`).size.width;
									const next = active + 1;
									icache.set('translateDelta', seatWidth! * next);
									icache.set('active', next);
								}
							},
							onKeyDown: (event) => {
								switch (event.key) {
									case Keys.Enter:
									case Keys.Space:
									case Keys.Spacebar:
										event.preventDefault();
										if (active < maxDelta) {
											const seatWidth = dimensions.get(`seat-${active}`).size.width;
											const next = active + 1;
											icache.set('translateDelta', seatWidth! * next);
											icache.set('active', next);
										}
								}
							}
						})
					])
				]),
				footnote ? v('div', { classes: [themeCss.note] }, [footnote]) : null
			]
		);
	}
);

export default Carousel;
